import { useEffect, useRef } from "react";
import type { ApolloCache, ApolloClient } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";

import { useDesignOptionId } from "./useDesignOptionId";

/**
 * Normalized ids option reads share with trunk. Anything else (Workspace,
 * feature flags) stays so WorkspaceChallenge does not remount the shell.
 */
const ORG_ENTITY_PREFIXES = [
  "Group:",
  "Person:",
  "Membership:",
  "IndividualAllocation:",
] as const;

/** Root fields whose cached graph must survive an option switch. */
const BOOTSTRAP_ROOT_FIELDS = new Set([
  "getWorkspace",
  "getActiveWorkspace",
  "getWorkspaces",
  "getPersonForUser",
  "getAllocationProject",
  "getVersion",
  "getMaintenanceMode",
  "getLatestImportDate",
  "getOldestPersonIndexDate",
  "getReportingInfraProvisioned",
  "getReportingResourceCreationStatus",
]);

const BOOTSTRAP_QUERY_NAMES = new Set([
  "getWorkspace",
  "getActiveWorkspace",
  "getWorkspaces",
  "getContext",
  // Not getReportingInfraProvisioned — workspace-scoped, can hit AWS Batch;
  // kept via BOOTSTRAP_ROOT_FIELDS only so option switches do not await it.
  "getReportingResourceCreationStatus",
]);

/**
 * Fields getWorkspace / getContext select. Option reads write the same
 * entities (name, type, isHidden, tags). Anything outside these sets is
 * dropped. The sets themselves are refetched so a previous option's values
 * do not stay on the shell.
 *
 * Kept in step with PartialGroupFragment, UserPersonFragment,
 * PersonManagersTagsFragment, and PersonCardFragment.
 */
type FieldSpec = {
  keep: readonly string[];
  follow?: Readonly<Record<string, FieldSpec>>;
};

const GROUP_PARTIAL: FieldSpec = { keep: ["id", "name", "type"] };

const GROUP_DIVISION: FieldSpec = {
  keep: ["id", "name", "hierarchy", "hierarchyIds"],
};

const GROUP_MEMBER_OF: FieldSpec = {
  keep: [
    "id",
    "parentIds",
    "name",
    "type",
    "hierarchyIds",
    "attributes",
    "permissions",
    "tags",
  ],
};

const GROUP_ACCESS: FieldSpec = {
  keep: [
    "id",
    "name",
    "type",
    "parentIds",
    "hierarchyIds",
    "attributes",
    "permissions",
  ],
};

const GROUP_CARD_MEMBER: FieldSpec = {
  keep: ["id", "name", "type", "hierarchy", "hierarchyIds"],
};

const GROUP_SUPPLY_OWNER: FieldSpec = {
  keep: [
    "id",
    "name",
    "type",
    "hierarchy",
    "hierarchyIds",
    "parentIds",
    "attributes",
  ],
};

const PERSON_MANAGER: FieldSpec = {
  keep: [
    "aggregateId",
    "firstName",
    "surname",
    "preferredName",
    "avatar",
    "jobTitle",
    "attributes",
  ],
};

const PERSON_HIERARCHY: FieldSpec = {
  keep: [
    "aggregateId",
    "firstName",
    "surname",
    "preferredName",
    "avatar",
    "jobTitle",
  ],
};

const PERSON_CARD: FieldSpec = {
  keep: [
    "aggregateId",
    "employeeId",
    "firstName",
    "surname",
    "preferredName",
    "jobTitle",
    "company",
    "fte",
    "span",
    "bands",
    "isVacancy",
    "filledPositions",
    "avatar",
    "attributes",
    "appliedTags",
    "tags",
    "permissions",
    "allocations",
  ],
  follow: {
    memberOf: GROUP_CARD_MEMBER,
    supplyOwnerOf: GROUP_SUPPLY_OWNER,
  },
};

const PERSON_USER: FieldSpec = {
  keep: [
    "address",
    "country",
    "city",
    "timezone",
    "aggregateId",
    "employeeId",
    "allocations",
    "attributes",
    "avatar",
    "bands",
    "company",
    "fte",
    "jobTitle",
    "firstName",
    "surname",
    "preferredName",
    "layers",
    "span",
    "sourceGroupIds",
    "accessGroupIds",
    "permissions",
    "appliedTags",
    "tags",
  ],
  follow: {
    division: GROUP_DIVISION,
    memberOf: GROUP_MEMBER_OF,
    accessGroups: GROUP_ACCESS,
    managedBy: PERSON_MANAGER,
    supplyManagedBy: PERSON_MANAGER,
    supplyInferredManagedBy: PERSON_CARD,
    managedByHierarchy: PERSON_HIERARCHY,
    supplyManagedByHierarchy: PERSON_HIERARCHY,
  },
};

const WORKSPACE_BOOTSTRAP: FieldSpec = {
  keep: [],
  follow: { rootGroups: GROUP_PARTIAL },
};

const BOOTSTRAP_SPECS: Partial<Record<string, FieldSpec>> = {
  getWorkspace: WORKSPACE_BOOTSTRAP,
  getActiveWorkspace: WORKSPACE_BOOTSTRAP,
  getPersonForUser: PERSON_USER,
};

type CacheRecord = Record<string, unknown>;

const fieldNameOf = (storeKey: string): string => {
  const paren = storeKey.indexOf("(");
  return paren === -1 ? storeKey : storeKey.slice(0, paren);
};

const collectRefs = (
  value: unknown,
  into: Set<string>,
  data: Record<string, CacheRecord>
): void => {
  if (!value || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      collectRefs(item, into, data);
    }
    return;
  }
  const ref = (value as { __ref?: unknown }).__ref;
  if (typeof ref === "string") {
    if (into.has(ref)) {
      return;
    }
    into.add(ref);
    const entity = data[ref];
    if (entity) {
      collectRefs(entity, into, data);
    }
    return;
  }
  for (const child of Object.values(value)) {
    collectRefs(child, into, data);
  }
};

const rememberKeep = (
  keepById: Map<string, Set<string>>,
  id: string,
  fields: readonly string[]
): void => {
  const set = keepById.get(id) ?? new Set(["__typename"]);
  for (const field of fields) {
    set.add(field);
  }
  keepById.set(id, set);
};

const valuesForField = (entity: CacheRecord, field: string): unknown[] => {
  const values: unknown[] = [];
  for (const [key, value] of Object.entries(entity)) {
    if (fieldNameOf(key) === field) {
      values.push(value);
    }
  }
  return values;
};

const visitSpec = (
  value: unknown,
  spec: FieldSpec,
  data: Record<string, CacheRecord>,
  keepById: Map<string, Set<string>>,
  walking: Set<string>
): void => {
  if (!value || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      visitSpec(item, spec, data, keepById, walking);
    }
    return;
  }
  const ref = (value as { __ref?: unknown }).__ref;
  if (typeof ref !== "string" || walking.has(ref)) {
    return;
  }
  rememberKeep(keepById, ref, spec.keep);
  if (!spec.follow) {
    return;
  }
  // getContext selects these edges. Keeping only spec.keep drops them, and
  // gc() then collects the groups and managers they point at.
  rememberKeep(keepById, ref, Object.keys(spec.follow));
  const entity = data[ref];
  if (!entity) {
    return;
  }
  walking.add(ref);
  for (const [field, child] of Object.entries(spec.follow)) {
    for (const childValue of valuesForField(entity, field)) {
      visitSpec(childValue, child, data, keepById, walking);
    }
  }
  walking.delete(ref);
};

/** Drop option-scoped org entities. Keep bootstrap fields on the shell graph. */
export const evictOptionScopedEntities = (cache: ApolloCache): void => {
  const data = cache.extract() as Record<string, CacheRecord>;
  const root = data["ROOT_QUERY"] ?? {};
  const fullyProtected = new Set<string>();
  const keepById = new Map<string, Set<string>>();
  for (const [key, value] of Object.entries(root)) {
    if (key === "__typename") {
      continue;
    }
    const fieldName = fieldNameOf(key);
    if (!BOOTSTRAP_ROOT_FIELDS.has(fieldName)) {
      continue;
    }
    const spec = BOOTSTRAP_SPECS[fieldName];
    if (spec) {
      visitSpec(value, spec, data, keepById, new Set());
    } else {
      collectRefs(value, fullyProtected, data);
    }
  }
  for (const id of Object.keys(data)) {
    if (fullyProtected.has(id) || !id.includes(":")) {
      continue;
    }
    const keep = keepById.get(id);
    if (!keep) {
      if (ORG_ENTITY_PREFIXES.some((prefix) => id.startsWith(prefix))) {
        cache.evict({ id });
      }
      continue;
    }
    if (!ORG_ENTITY_PREFIXES.some((prefix) => id.startsWith(prefix))) {
      continue;
    }
    const entity = data[id];
    if (!entity) {
      continue;
    }
    const drop = new Set<string>();
    for (const storeKey of Object.keys(entity)) {
      const name = fieldNameOf(storeKey);
      if (!keep.has(name)) {
        drop.add(name);
      }
    }
    for (const fieldName of drop) {
      cache.evict({ id, fieldName });
    }
  }
  cache.gc();
};

/**
 * client.query writes trunk fields into the cache without moving the mounted
 * useQuery into loading. refetchQueries would, and WorkspaceChallenge remounts
 * the shell whenever getWorkspace's loading flag is true.
 */
const refreshBootstrapQueries = (client: ApolloClient): Promise<unknown> => {
  const refreshes: Promise<unknown>[] = [];
  for (const observable of client.getObservableQueries("active")) {
    const name = observable.queryName;
    if (!name || !BOOTSTRAP_QUERY_NAMES.has(name)) {
      continue;
    }
    refreshes.push(
      client
        .query({
          query: observable.options.query,
          variables: observable.variables,
          fetchPolicy: "network-only",
        })
        .catch(() => undefined)
    );
  }
  return Promise.all(refreshes);
};

const refetchActiveOptionQueries = (client: ApolloClient): Promise<unknown> =>
  client
    .refetchQueries({
      include: "active",
      onQueryUpdated: (observable) => {
        const name = observable.queryName;
        return !(name && BOOTSTRAP_QUERY_NAMES.has(name));
      },
    })
    .catch(() => undefined);

/**
 * Option reads and trunk reads share normalized Apollo entries (Group:<id>,
 * Person:<id>). On option change, drop org entities the shell does not hold,
 * strip option-only fields from the ones it does, refetch the shell queries
 * so name, type and tags come back from trunk, then refetch active option
 * queries. getWorkspace stays in the cache, so WorkspaceChallenge does not
 * remount the shell.
 *
 * Mounted once in AppLayout, which renders in every mode (the header switcher
 * does not mount with hideHeader or embed).
 */
export const useResetStoreOnDesignOptionChange = (): void => {
  const client = useApolloClient();
  const { optionId } = useDesignOptionId();
  const previousOptionId = useRef(optionId);

  useEffect(() => {
    if (previousOptionId.current === optionId) {
      return;
    }
    previousOptionId.current = optionId;
    let cancelled = false;
    evictOptionScopedEntities(client.cache);
    refreshBootstrapQueries(client)
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) {
          return refetchActiveOptionQueries(client);
        }
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, optionId]);
};
